<template>
  <section v-if="localBudgetPrice !== undefined" id="budget-price-form">
    <VInputNumberRusV2
      v-model="localBudgetPrice.price"
      label="Бюджетная цена"
      :precision="3"
      :required="required"
      :disabled="isControlsDisabled"
      :disabled-reasons="disabledReasons"
      :unit="localBudgetPrice?.currency?.toString({ format: 'symbol' })"
      :type="Price"
    />

    <VSelectV2
      v-model="localBudgetPrice.currency"
      label="Валюта бюджетной цены"
      :data="$core.currencies.currencies"
      :required="required"
      :disabled="isControlsDisabled"
      :disabled-reasons="disabledReasons"
    />
  </section>
</template>

<script lang="ts">

import BudgetPrice from '@/domains/BudgetPrices/BudgetPrice';
import BudgetPricesService from '@/domains/BudgetPrices/BudgetPricesService';

import Price from '@/core/types/Price';

import type Product from '@/domains/Products/Product';

/**
 * Контролы бюджетной цены. Два сценария использования:
 *
 * 1. Карточка бюджетной цены (BudgetPriceCard) — цена там самостоятельная сущность:
 *    карточка её грузит, валидирует и сохраняет сама, форме отдаёт готовый объект
 *    пропом budgetPrice. submit() у формы не зовётся.
 *
 * 2. Карточка номенклатуры (ProductCard) и карточка разрешения (SupplyOfferCard) —
 *    цена там довесок к своей сущности: передаётся товар, форма сама грузит цену по нему
 *    и сама сохраняет её в submit(), который родитель зовёт после успешного сохранения
 *    своей сущности (у нового товара id появляется только там).
 */
export default defineComponent({
  name: 'BudgetPriceForm',

  props: {
    /** Сценарий 1: готовая цена снаружи — форма её только показывает и правит */
    budgetPrice: {
      type: Object as PropType<BudgetPrice>,
      default: null,
    },

    /** Сценарий 2: товар, по которому форма сама грузит и сохраняет цену */
    product: {
      type: Object as PropType<Product>,
      default: null,
    },

    /** Цену удалённой сущности не правят */
    disabled: {
      type: Boolean,
      default: false,
    },

    /** В карточке бюджетной цены оба поля обязательны, в довесках — нет */
    required: {
      type: Boolean,
      default: false,
    },
  },

  /** Цена нужна снаружи смежным контролам (сетка тарифов разрешения) — отдаём её через v-model:budget-price */
  emits: ['update:budgetPrice'],

  setup() {
    return {
      Price,
    };
  },

  data() {
    return {
      /** Цена, с которой работают контролы: пришедшая пропом либо загруженная самой формой */
      localBudgetPrice: undefined as unknown as BudgetPrice,

      // Бюджетная цена на момент загрузки. Сравниваем с текущей на сабмите, чтобы не слать запрос, если пользователь её не менял
      initialBudgetPrice: undefined as unknown as Price,

      // Валюта бюджетной цены на момент загрузки — сравниваем по коду, объект валюты после перевыбора в селекте другой инстанс
      initialBudgetPriceCurrencyCode: undefined as unknown as string,

      isLoading: false,
    };
  },

  computed: {
    /** Правило одно на оба контрола: у несохранённого товара цену вводить некуда — привязывать не к чему */
    isControlsDisabled(): boolean {
      if (this.disabled === true || this.isLoading === true) return true;
      if (this.localBudgetPrice?.product === null) return true;
      if (this.localBudgetPrice?.product.isExisting === false) return true;
      return false;
    },

    /** Причина, по которой поля не дают заполнить, — контрол показывает её пользователю */
    disabledReasons(): string[] {
      if (this.localBudgetPrice?.product === null || this.localBudgetPrice?.product?.isExisting === false) {
        return ['Товар ещё не сохранён — бюджетную цену не к чему привязать'];
      } else {
        return [];
      }
    },
  },

  async created() {
    /** Цену дают снаружи — грузить нечего */
    if (this.$core.utils.isNullOrUndefined(this.budgetPrice) === false && this.budgetPrice.isExisting === true) {
      this.localBudgetPrice = this.budgetPrice;
    } else {
      await this.load();
    }
  },

  methods: {
    /** Загружает бюджетную цену товара.
     * Если товара нет или цена ещё не заводилась — пустая цена с привязкой к товару.
     * Запоминает исходные значения цены и её валюты для последующего сравнения на сабмите
     *
     * Товар передаётся параметром, когда его меняют прямо в карточке: проп на момент
     * события селекта ещё старый, обновится только на следующем рендере
     */
    async load(product: Product | null = this.product): Promise<void> {
      this.isLoading = true;

      if (this.$core.utils.isNullOrUndefined(product?.id) === true) {
        this.localBudgetPrice = BudgetPrice.fromNew();
      } else {
        const existing = await BudgetPricesService.get(product!.id);

        if (this.$core.utils.isNullOrUndefined(existing) === false) {
          this.localBudgetPrice = existing!;
        } else {
          this.localBudgetPrice = BudgetPrice.fromNew();
          this.localBudgetPrice.product = product!;
        }
      }

      this.initialBudgetPrice = this.localBudgetPrice.price;
      this.initialBudgetPriceCurrencyCode = this.localBudgetPrice.currency?.code!;

      this.isLoading = false;

      this.$emit('update:budgetPrice', this.localBudgetPrice);
    },

    /**
     * Сохраняет бюджетную цену, только если пользователь изменил её значение или валюту.
     * Сценарий 2: зовётся родителем через ref после успешного сохранения его сущности.
     * В сценарии 1 не зовётся вовсе — там сохраняет карточка.
     */
    async submit(): Promise<boolean> {
      if (this.isLoading === true) {
        return false;
      }

      const isPriceChanged = this.localBudgetPrice.price !== this.initialBudgetPrice;
      const isCurrencyChanged = this.localBudgetPrice.currency?.code !== this.initialBudgetPriceCurrencyCode;

      if (isPriceChanged === false && isCurrencyChanged === false) {
        return false;
      } else if (this.$core.utils.isNullOrUndefined(this.localBudgetPrice.price) === true) {
        return false;
      } else if (this.$core.utils.isNullOrUndefined(this.product?.id) === true) {
        return false;
      } else {
        /** Товар мог получить id только что — при создании из карточки товара */
        this.localBudgetPrice.product = this.product;

        return await BudgetPricesService.upsert(this.localBudgetPrice);
      }
    },
  },
});
</script>
